import { nextTick } from "vue";

// Local replacement for `vue3-observe-visibility`.
//
// Why: that package's published ESM bundle includes Vue runtime code, which can load a second
// Vue instance in dev and break Vue HMR (template/script changes don't apply while CSS HMR still works).
// We keep the same directive name (`v-observe-visibility`) and behavior, but rely on the app's Vue.

function processOptions(value) {
  if (typeof value === "function") {
    return { callback: value };
  }
  return value;
}

function throttle(callback, delay, options = {}) {
  let timeout;
  let lastState;
  let currentArgs;

  const throttled = (state, ...args) => {
    currentArgs = args;
    if (timeout && state === lastState) return;

    let leading = options.leading;
    if (typeof leading === "function") {
      leading = leading(state, lastState);
    }

    if ((!timeout || state !== lastState) && leading) {
      callback(state, ...currentArgs);
    }

    lastState = state;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback(state, ...currentArgs);
      timeout = 0;
    }, delay);
  };

  throttled._clear = () => {
    clearTimeout(timeout);
    timeout = null;
  };

  return throttled;
}

function deepEqual(val1, val2) {
  if (val1 === val2) return true;
  if (typeof val1 === "object") {
    for (const key in val1) {
      if (!deepEqual(val1[key], val2[key])) return false;
    }
    return true;
  }
  return false;
}

class VisibilityState {
  constructor(el, options) {
    this.el = el;
    this.observer = null;
    this.frozen = false;
    this.options = null;
    this.callback = null;
    this.oldResult = void 0;
    this.createObserver(options);
  }

  get threshold() {
    return (this.options && this.options.intersection && this.options.intersection.threshold) || 0;
  }

  createObserver(options) {
    if (this.observer) this.destroyObserver();
    if (this.frozen) return;

    this.options = processOptions(options);

    this.callback = (result, entry) => {
      this.options.callback(result, entry);
      if (result && this.options.once) {
        this.frozen = true;
        this.destroyObserver();
      }
    };

    // Throttle
    if (this.callback && this.options.throttle) {
      const { leading } = this.options.throttleOptions || {};

      this.callback = throttle(this.callback, this.options.throttle, {
        leading: (state) => {
          return leading === "both" || (leading === "visible" && state) || (leading === "hidden" && !state);
        },
      });
    }

    this.oldResult = void 0;

    this.observer = new IntersectionObserver((entries) => {
      let entry = entries[0];

      if (entries.length > 1) {
        const intersectingEntry = entries.find((e) => e.isIntersecting);
        if (intersectingEntry) entry = intersectingEntry;
      }

      if (this.callback) {
        // Use isIntersecting if possible because browsers can report isIntersecting as true,
        // but intersectionRatio as 0, when something very slowly enters the viewport.
        const result = entry.isIntersecting && entry.intersectionRatio >= this.threshold;

        if (result === this.oldResult) return;
        this.oldResult = result;
        this.callback(result, entry);
      }
    }, this.options.intersection);

    // Wait for the element to be in document
    nextTick(() => {
      if (this.observer) this.observer.observe(this.el);
    });
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Cancel throttled call
    if (this.callback && this.callback._clear) {
      this.callback._clear();
      this.callback = null;
    }
  }
}

export const ObserveVisibility = {
  beforeMount(el, { value }) {
    if (!value) return;
    if (typeof IntersectionObserver === "undefined") {
      console.warn("[observe-visibility] IntersectionObserver API is not available in your browser.");
      return;
    }
    el._vue_visibilityState = new VisibilityState(el, value);
  },
  updated(el, { value, oldValue }) {
    if (deepEqual(value, oldValue)) return;

    const state = el._vue_visibilityState;
    if (!value) return;
    if (state) state.createObserver(value);
  },
  unmounted(el) {
    const state = el._vue_visibilityState;
    if (!state) return;
    state.destroyObserver();
    delete el._vue_visibilityState;
  },
};

export default {
  install(app) {
    app.directive("observe-visibility", ObserveVisibility);
  },
};
